import { useGraphQLQuery } from '@betterment/js-runtime';
import { Box, Button, Dialog, Heading } from '@betterment/samba-react';
import { ErrorBoundary } from '@betterment/shared-components';
import { useRequiredParam } from '@betterment/shared-components/src/hooks/useRequiredParam';

import { CashAccountSettings } from './components/CashAccountSettings';
import { DirectTradingAccountSettings } from './components/DirectTradingAccountSettings';
import { ManagedAccountSettings } from './components/ManagedAccountSettings';
import { EnvelopeAccountSettingsModalDocument } from './graphql/accountSettingsModal.generated';

import { IdempotencyProviderWithFeatureRegistry } from '../../../RetailApp/graphql/idempotency/IdempotencyProviderWithFeatureRegistry';
import { ModalSectionLoading } from '../../../Shared/components-v2/modal-section/components/ModalSectionLoading';

export const AccountSettingsModalView: React.FC = () => {
  const accountId = useRequiredParam('accountId');
  const { data } = useGraphQLQuery(EnvelopeAccountSettingsModalDocument, {
    variables: { id: accountId },
    throwOnError: true,
  });

  if (!data) {
    return (
      <Box css={{ display: 'grid', gap: 'space.200', mt: 'space.200' }}>
        <ModalSectionLoading />
        <ModalSectionLoading />
        <ModalSectionLoading />
      </Box>
    );
  }

  if (!data.envelopeAccount) return null;

  const { envelopeAccount } = data;

  if (envelopeAccount.__typename === 'CashAccount') {
    return <CashAccountSettings envelopeAccount={envelopeAccount} />;
  }

  if (envelopeAccount.__typename === 'ManagedAccount') {
    return <ManagedAccountSettings envelopeAccount={envelopeAccount} />;
  }

  if (envelopeAccount.__typename === 'DirectTradingAccount') {
    return <DirectTradingAccountSettings envelopeAccount={envelopeAccount} />;
  }

  return null;
};

export const AccountSettingsModal: React.FC = () => {
  return (
    <IdempotencyProviderWithFeatureRegistry featureName="deletePurpose">
      <Dialog>
        <Dialog.Trigger asChild={true}>
          <Button variant="ghostSecondary">
            <Button.Icon name="Gear" />
            Settings
          </Button>
        </Dialog.Trigger>
        <Dialog.Content
          css={{
            display: 'grid',
            gap: 'space.100',
            p: 'space.400',
            overflowY: 'auto',
          }}
          size="large"
        >
          <Dialog.Title id="account-settings-modal" asChild={true}>
            <Heading as="h2" variant="title300">
              Account settings
            </Heading>
          </Dialog.Title>
          <ErrorBoundary fallbackType="partial">
            <AccountSettingsModalView />
          </ErrorBoundary>
        </Dialog.Content>
      </Dialog>
    </IdempotencyProviderWithFeatureRegistry>
  );
};
